import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  GraduationCap, 
  Eye, 
  EyeSlash, 
  ArrowRight,
  ArrowLeft,
  CircleNotch,
  ShieldCheck,
  Briefcase,
  HardHat,
  Buildings
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/1szu61lb_Logo%20G%26M.png";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_gm-class-training/artifacts/oozvwwjn_Editar%20Foto.png";

const ROLE_CONFIG = {
  admin: { icon: ShieldCheck, color: '#2B4B8C', label: 'Administrador' },
  contractor: { icon: Briefcase, color: '#2BBAA4', label: 'Contratista' },
  worker: { icon: HardHat, color: '#f59e0b', label: 'Trabajador' },
  client: { icon: Buildings, color: '#8b5cf6', label: 'Mandante' },
};

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [roleOptions, setRoleOptions] = useState(null);
  const { login, loginWithRole } = useAuth();
  const navigate = useNavigate();

  const redirectByRole = (role) => {
    switch (role) {
      case 'admin': navigate('/admin'); break;
      case 'contractor': navigate('/contractor'); break;
      case 'worker': navigate('/worker'); break;
      case 'client': navigate('/client'); break;
      case 'relator': navigate('/relator'); break;
      default: navigate('/');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.requires_role_selection) {
        setRoleOptions(result.roles);
        setLoading(false);
        return;
      }
      
      toast.success(`Bienvenido, ${result.first_name}!`);
      redirectByRole(result.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelect = async (userId) => {
    setLoading(true);
    try {
      const user = await loginWithRole(userId);
      toast.success(`Bienvenido, ${user.first_name}!`);
      redirectByRole(user.role);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar sesion');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setRoleOptions(null);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-8 lg:px-16 xl:px-24 bg-white">
        <div className="max-w-md w-full mx-auto">
          {/* Logo */}
          <div className="mb-12">
            <img 
              src={LOGO_URL} 
              alt="G&M Class" 
              className="h-20 object-contain"
              data-testid="login-logo"
            />
          </div>

          {!roleOptions ? (
            <>
              {/* Welcome Text */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-[#2B4B8C] mb-2">
                  Bienvenido a G&M Class
                </h1>
                <p className="text-[#2B4B8C]/60">
                  Ingresa tus credenciales para acceder a la plataforma
                </p>
              </div>

              {/* Login Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#2B4B8C]">
                    Correo electronico
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="correo@ejemplo.com"
                    className="bg-white border-[#2B4B8C]/20 text-[#2B4B8C] placeholder:text-[#2B4B8C]/40 focus:border-[#2BBAA4] focus:ring-[#2BBAA4] h-12"
                    required
                    data-testid="login-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#2B4B8C]">
                    Contrasena
                  </Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="********"
                      className="bg-white border-[#2B4B8C]/20 text-[#2B4B8C] placeholder:text-[#2B4B8C]/40 focus:border-[#2BBAA4] focus:ring-[#2BBAA4] h-12 pr-12"
                      required
                      data-testid="login-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-[#2B4B8C]/50 hover:text-[#2B4B8C] transition-colors"
                    >
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-[#2BBAA4] text-white hover:bg-[#239986] font-semibold shadow-[0_0_15px_rgba(43,186,164,0.3)] hover:shadow-[0_0_25px_rgba(43,186,164,0.5)] transition-all duration-300"
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <CircleNotch className="animate-spin mr-2" size={20} />
                  ) : (
                    <>
                      Iniciar Sesion
                      <ArrowRight className="ml-2" size={20} />
                    </>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              {/* Role Selection */}
              <div className="mb-8">
                <h1 className="text-3xl font-bold text-[#2B4B8C] mb-2">
                  Selecciona tu perfil
                </h1>
                <p className="text-[#2B4B8C]/60">
                  Tu cuenta tiene multiples perfiles. Elige con cual deseas ingresar.
                </p>
              </div>

              <div className="space-y-3" data-testid="role-selection-list">
                {roleOptions.map((role) => {
                  const config = ROLE_CONFIG[role.role] || ROLE_CONFIG.worker;
                  const Icon = config.icon;
                  return (
                    <button
                      key={role.user_id}
                      onClick={() => handleRoleSelect(role.user_id)}
                      disabled={loading}
                      className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-[#2B4B8C]/10 hover:border-[#2BBAA4] hover:bg-[#2BBAA4]/5 transition-all duration-200 text-left group"
                      data-testid={`role-option-${role.role}`}
                    >
                      <div 
                        className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: `${config.color}15` }}
                      >
                        <Icon size={24} style={{ color: config.color }} weight="duotone" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[#2B4B8C] group-hover:text-[#2BBAA4] transition-colors">
                          {role.role_label}
                        </div>
                        <div className="text-sm text-[#2B4B8C]/50 truncate">
                          {role.name}{role.company_name ? ` - ${role.company_name}` : ''}
                        </div>
                      </div>
                      <ArrowRight size={18} className="text-[#2B4B8C]/30 group-hover:text-[#2BBAA4] transition-colors shrink-0" />
                    </button>
                  );
                })}
              </div>

              <Button
                variant="ghost"
                onClick={handleBackToLogin}
                className="w-full mt-6 text-[#2B4B8C]/60 hover:text-[#2B4B8C]"
                data-testid="back-to-login-btn"
              >
                <ArrowLeft size={18} className="mr-2" />
                Volver al login
              </Button>
            </>
          )}

          {/* Validate Certificate Link */}
          <div className="mt-8 pt-8 border-t border-[#2B4B8C]/10">
            <Link
              to="/validate"
              className="flex items-center justify-center gap-2 text-[#2B4B8C]/60 hover:text-[#2BBAA4] transition-colors"
              data-testid="validate-certificate-link"
            >
              <GraduationCap size={20} />
              <span>Validar un certificado</span>
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Hero Image */}
      <div 
        className="hidden lg:block lg:w-1/2 relative bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/20" />
        
        {/* Overlay Content */}
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="text-2xl font-bold text-white mb-2">
            Plataforma de Capacitaciones Corporativas
          </h2>
          <p className="text-white/80">
            Gestiona el ciclo completo de capacitaciones: cursos, evaluaciones, 
            certificaciones y reportabilidad en un solo lugar.
          </p>
        </div>
      </div>
    </div>
  );
}
